import {
  Button,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import ManageSearchIcon from "@mui/icons-material/ManageSearch";
import { React, useState, useEffect } from "react";
import Alert from "../../components/ui/Alert";
import { sizes } from "./dashboardUtils";
import { useDispatch, useSelector } from "react-redux";
import { getApprovalStats, getScenarios, setState } from "./dashBoardSlice";
import PendingActions from "./PendingActions";
import RunningPeriodStatus from "./RunningPeriodStatus";

const selectWidth = sizes.w200;
const selectHeight = sizes.h35;

const orderStatusList = ["All", "Normal", "Blocked", "Blocked and Reviewed"];

export default function SearchForm() {
  const dispatch = useDispatch();
  const state = useSelector((state) => state.dashboard);
  const {
    drawerOpen,
    selectedCountry,
    selectedCountryID,
    countries,
    selectedCriticality,
    selectedCriticalityID,
    selectedOrderStatus,
    scenario,
    scenarioCriticalityList,
  } = state;

  const [validate, setValidate] = useState({
    countryValidated: false,
    criticalityValidated: false,
    orderStatusValidated: false,
  });

  const [alert, setAlert] = useState({
    alertSeverity: "",
    alertDisplay: false,
    alertMessage: "",
  });

  const displayAlert = (showAlert, alertMessage) => {
    setAlert({
      alertSeverity: "warning",
      alertDisplay: `${showAlert}`,
      alertMessage: `${alertMessage}`,
    });
  };

  useEffect(() => {
    dispatch(setState("selectedCountry", ""));
    dispatch(setState("selectedCountryID", ""));
    dispatch(setState("selectedCriticality", ""));
    dispatch(setState("selectedCriticalityID", ""));
    dispatch(setState("selectedOrderStatus", ""));
  }, []);

  useEffect(() => {
    //Check if any of the dropdown are false and then set alertDisplay to false
    const isAnyKeyValueFalse = (o) => !!Object.keys(o).find((k) => !o[k]);

    if (isAnyKeyValueFalse(validate)) {
      setAlert({ ...Alert, alertDisplay: false });
    }
  }, [validate]);

  function setCountry(event) {
    const name = event.target.value;

    if (name) {
      setValidate({
        ...validate,
        countryValidated: true,
      });
    }

    const countryID = countries.find(
      (country) => country.countryName === name
    ).countryID;

    dispatch(setState("selectedCountry", name));
    dispatch(setState("selectedCountryID", countryID));
  }

  function setCriticality(event) {
    const name = event.target.value;
    let criticalityID = 0;
    if (name == "All") criticalityID = 0;
    else
      criticalityID = scenarioCriticalityList.find(
        (criticality) => criticality.scenarioCriticalityDesc === name
      ).scenarioCriticalityID;

    if (name) {
      setValidate({
        ...validate,
        criticalityValidated: true,
      });
    }

    dispatch(setState("selectedCriticality", name));
    dispatch(setState("selectedCriticalityID", criticalityID));
  }

  function setOrderStatus(event) {
    const status = event.target.value;

    if (status) {
      setValidate({
        ...validate,
        orderStatusValidated: true,
      });
    }

    dispatch(setState("selectedOrderStatus", status));
  }

  function setScenario(event) {
    const scenario = event.target.value;
    dispatch(setState("scenario", scenario));
  }

  function handleSearch() {
    if (!validate.countryValidated)
      displayAlert(true, "Please select a Country");
    else if (!validate.criticalityValidated)
      displayAlert(true, "Please select a Criticality");
    else if (!validate.orderStatusValidated)
      displayAlert(true, "Please select a Order Status");
    else {
      const getScenarioParams = {
        countryID: selectedCountryID,
        status: selectedOrderStatus,
        scenario: scenario,
        criticalityId: selectedCriticalityID,
      };

      const getApprovalStatsParams = {
        countryID: selectedCountryID,
      };

      dispatch(getScenarios(getScenarioParams));
      dispatch(getApprovalStats(getApprovalStatsParams));
    }
  }

  return (
    <Paper
      elevation={2}
      sx={{
        width: drawerOpen ? "75%" : "100%",
        p: 2,
        borderRadius: 2,
        mt: 3,
      }}
    >
      <Typography variant="subtitle1" sx={{ mt: 1, mb: 2 }}>
        Search for Scenario
      </Typography>
      <Grid
        container
        columns={12}
        spacing={2}
        sx={{ justifyContent: "space-between" }}
      >
        <Grid item sm={6} md={3} lg={2.5}>
          <FormControl fullWidth>
            <InputLabel>Country</InputLabel>
            <Select
              value={selectedCountry}
              label="Country"
              sx={{
                height: selectHeight,
                width: "100%",
                color: "black.main",
                mt: 1,
              }}
              onChange={setCountry}
            >
              {countries.map((country, index) => {
                return (
                  <MenuItem key={index} value={country.countryName}>
                    {country.countryName}
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>
        </Grid>

        <Grid item sm={6} md={3} lg={2.5}>
          <FormControl fullWidth>
            <InputLabel>Scenario Criticality</InputLabel>
            <Select
              value={selectedCriticality}
              label="Scenario Criticality"
              sx={{
                height: selectHeight,
                width: "100%",
                color: "black.main",
                mt: 1,
              }}
              onChange={setCriticality}
            >
              <MenuItem key={0} value="All">
                All
              </MenuItem>
              {scenarioCriticalityList.map((criticality, index) => {
                return (
                  <MenuItem
                    key={criticality.scenarioCriticalityID}
                    value={criticality.scenarioCriticalityDesc}
                  >
                    {criticality.scenarioCriticalityDesc}
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>
        </Grid>

        <Grid item sm={6} md={3} lg={2.5}>
          <FormControl fullWidth>
            <InputLabel>Order Status</InputLabel>
            <Select
              value={selectedOrderStatus}
              label="Order Status"
              sx={{
                height: selectHeight,
                width: "100%",
                color: "black.main",
                mt: 1,
              }}
              onChange={setOrderStatus}
            >
              {orderStatusList.map((status, index) => {
                return (
                  <MenuItem key={index} value={status}>
                    {status}
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>
        </Grid>

        <Grid item sm={6} md={3} lg={2.5}>
          <TextField
            fullWidth
            label="Scenario"
            variant="standard"
            sx={{
              width: selectWidth,
            }}
            value={scenario}
            onChange={setScenario}
          />
        </Grid>

        <Grid item sm={2} md={2} lg={1.2}>
          <Button
            startIcon={<ManageSearchIcon />}
            sx={{
              bgcolor: "blue.main",
              color: "white.main",
              textTransform: "none",
              fontWeight: "bold",
              mt: 1,
              "&:hover": {
                bgcolor: "skyBlue.main",
                color: "black.main",
              },
            }}
            onClick={handleSearch}
          >
            Search
          </Button>
        </Grid>
      </Grid>
      {alert.alertDisplay && (
        <Alert message={alert.alertMessage} severity={alert.alertSeverity} />
      )}
      <Grid container columns={12} sx={{ justifyContent: "space-between" }}>
        <Grid sm={8.8} item>
          <RunningPeriodStatus />
        </Grid>
        <Grid sm={3} item>
          <PendingActions selectedCountryID={selectedCountryID} />
        </Grid>
      </Grid>
    </Paper>
  );
}
